import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { getStoredTokens, login as apiLogin, register as apiRegister, logout as apiLogout } from '../api/client';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  email: string | null;
  userId: string | null;
}

interface AuthContextValue extends AuthState {
  login:    (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout:   () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    email: null,
    userId: null,
  });

  // Check existing token on mount
  useEffect(() => {
    const { accessToken, email, userId } = getStoredTokens();
    setState({ isAuthenticated: !!accessToken, isLoading: false, email, userId });
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const data = await apiLogin(email, password);
    setState({ isAuthenticated: true, isLoading: false, email, userId: data.userId });
  }, []);

  const register = useCallback(async (email: string, password: string) => {
    const data = await apiRegister(email, password);
    setState({ isAuthenticated: true, isLoading: false, email, userId: data.userId });
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setState({ isAuthenticated: false, isLoading: false, email: null, userId: null });
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
